import { FC, KeyboardEvent, RefObject } from 'react';
import {
  CircularProgress,
  InputAdornment,
  TextField,
} from '@mui/material';

import Icon from '@/components/ui/Icon';
import { bm } from '@/theme/bausteinMasse';

/**
 * Design-System Teil B (DESIGN_SYSTEM.md, Phase 4a):
 * Einheitliches Suchfeld — Suchen-Icon links, optional Loading-
 * Spinner rechts. 36px Höhe, 8px Radius, 14px Schrift, divider-
 * Border (statt Hardcode-#eee).
 *
 * Debounce/Fetch bleiben bewusst außerhalb — der Aufrufer
 * kontrolliert wann und wie gesucht wird.
 */
export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  loading?: boolean;
  width?: number | string;
  autoFocus?: boolean;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  inputRef?: RefObject<HTMLInputElement>;
  'aria-label'?: string;
  /** Feldhoehe in px (Default aus dem Theme: CRM 36). Die Kopfleiste nutzt 32 (Korrekturrunde 2). */
  hoehe?: number;
}

const SearchField: FC<SearchFieldProps> = ({
  value,
  onChange,
  placeholder = 'Suchen...',
  loading = false,
  width,
  autoFocus,
  onKeyDown,
  inputRef,
  'aria-label': ariaLabel,
  hoehe,
}) => (
  <TextField
    inputRef={inputRef}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    autoFocus={autoFocus}
    size="small"
    inputProps={{ 'aria-label': ariaLabel ?? placeholder }}
    sx={{
      width,
      '& .MuiOutlinedInput-root': {
        // Masse aus `theme.bausteinMasse.eingabe` (Alt-Theme der Strecke: Original).
        fontSize: (t) => bm(t).eingabe.schrift,
        borderRadius: (t) => bm(t).eingabe.radius,
        bgcolor: 'background.paper',
        // Theme-Override (theme/input.tsx) lockt Single-line-Inputs
        // per `&:not(.MuiInputBase-multiline)` auf 44px (0,2,0) und
        // gewinnt Gleichstaende gegen Wrapper-sx. Gleiche :not()-
        // Klausel hier ergibt (0,3,0) — ohne !important.
        '&:not(.MuiInputBase-multiline)': { height: hoehe ?? ((t) => bm(t).eingabe.hoehe) },
        '& fieldset': { borderColor: 'divider' },
        '&:hover fieldset': {
          borderColor: (t) => `${t.palette.text.primary}3D`, // ~24% alpha
        },
        '&.Mui-focused fieldset': {
          borderColor: 'primary.main',
          borderWidth: 1,
        },
        // Font-Size (16px) + Placeholder setzt das Theme direkt auf
        // .MuiInputBase-input (0,2,0) — deshalb auch hier auf dem
        // Input, unterhalb des Root-Selektors (0,3,0).
        '& .MuiInputBase-input': {
          py: '6px',
          px: 0,
          fontSize: (t) => bm(t).eingabe.schrift,
          '&::placeholder': {
            color: 'text.primary',
            opacity: 0.55,
          },
        },
      },
    }}
    slotProps={{
      input: {
        startAdornment: (
          <InputAdornment position="start" sx={{ pl: '10px', mr: 1 }}>
            <Icon icon="Search" size={16} sx={{ color: 'grey.200' }} />
          </InputAdornment>
        ),
        endAdornment: loading ? (
          <InputAdornment position="end" sx={{ pr: '10px' }}>
            <CircularProgress size={14} sx={{ color: 'grey.200' }} />
          </InputAdornment>
        ) : undefined,
      },
    }}
  />
);

export default SearchField;
